import React, { useCallback, useMemo, useRef, useState } from 'react'

import * as Y from 'yjs'

import { FieldEditorPopover } from 'features/admin/fields/FieldEditorPopover'
import { FieldIcon } from 'features/admin/fields/icons/FieldIcon'
import { getFieldIcon } from 'features/admin/fields/icons/utils'
import { toYType } from 'features/utils/useYjsState'
import * as yUtils from 'features/utils/yUtils'
import {
    DetailViewTabControl,
    DetailViewTabControlItem,
} from 'features/views/DetailView/DetailViewTabControl'
import { LayoutEditorControlSeparator } from 'features/views/LayoutEditor/controls/LayoutEditorControlSeparator'
import { LayoutEditorVisibilityFiltersControl } from 'features/views/LayoutEditor/controls/LayoutEditorVisibilityFiltersControl'
import { WidgetAdminControlsComponent } from 'features/views/LayoutEditor/types'
import { useLayoutEditorContext } from 'features/views/LayoutEditor/useLayoutEditorContext'

import FieldListEditor from 'v2/ui/components/FieldsEditor/FieldListEditor'
import { Item } from 'v2/ui/components/OrderableListSelector/types'
import useDebounce from 'v2/ui/utils/useDebounce'

import { Box } from 'ui/components/Box'
import { Button } from 'ui/components/Button'
import { Field } from 'ui/components/Field'
import { IconPickerDropdown } from 'ui/components/IconPicker'
import { IconValue } from 'ui/components/IconPicker/IconPickerDropdown'
import { Input } from 'ui/components/Input'
import { RadioCard, RadioCardGroup } from 'ui/components/Radio'
import { Headline } from 'ui/components/Text'
import { Toggle } from 'ui/components/Toggle'

import { useHighlightsWidgetItems } from './hooks/useHighlightsWidgetItems'
import { HighlightsWidgetItem, HighlightsWidgetType } from './types'

type HighlightsWidgetAdminControlsProps = {}

export const HighlightsWidgetAdminControls: WidgetAdminControlsComponent<
    HighlightsWidgetType,
    HighlightsWidgetAdminControlsProps
> = ({ widget, onChange, ...props }) => {
    return (
        <DetailViewTabControl>
            <DetailViewTabControlItem value="content" label="Content">
                <HighlightsContentsControls widget={widget} onChange={onChange} {...props} />
            </DetailViewTabControlItem>
            <DetailViewTabControlItem value="style" label="Style">
                <HighlightsStyleControls widget={widget} onChange={onChange} {...props} />
            </DetailViewTabControlItem>
        </DetailViewTabControl>
    )
}

type FieldItem = Item & {
    fieldId: string
    fieldName: string
    conditions: Filter[]
    fieldLabel: string
    originalItem?: HighlightsWidgetItem
}

type HighlightsContentsControlsProps = {}

const HighlightsContentsControls: WidgetAdminControlsComponent<
    HighlightsWidgetType,
    HighlightsContentsControlsProps
> = ({ widget, onChange, openDetailPane }) => {
    const { object, fields } = useLayoutEditorContext()

    const addFieldTargetRef = useRef<HTMLButtonElement>(null)

    const [isAddFieldOpen, setIsAddFieldOpen] = useState(false)

    const onAddFieldClick = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            setIsAddFieldOpen((prev) => !prev)
            e.preventDefault()
            e.stopPropagation()
        },
        [setIsAddFieldOpen]
    )

    const onAddFieldClose = useCallback(() => {
        setIsAddFieldOpen(false)
    }, [setIsAddFieldOpen])

    const { displayedItems } = useHighlightsWidgetItems(widget)

    const onItemUpdate = useCallback(
        (items: FieldItem[]) => {
            onChange((attrs) => {
                const newFields: HighlightsWidgetItem[] = items.map((i) => {
                    if (!i.originalItem) {
                        return {
                            sid: i.fieldId,
                            apiName: i.fieldName,
                            conditions: i.conditions ?? [],
                        }
                    }

                    return {
                        ...i.originalItem,
                    }
                })

                attrs.set('items', toYType(newFields))
            })
        },
        [onChange]
    )

    const onItemEdit = useCallback(
        (item: FieldItem) => {
            openDetailPane({
                component: (props) => <ItemDetailsControl {...props} itemSid={item.fieldId} />,
                label: `Field: ${item.fieldLabel}`,
            })
        },
        [openDetailPane]
    )

    const conditionalVisibilityFilters = useMemo(() => {
        return displayedItems.reduce(
            (acc, f) => {
                acc[f.sid] = f.conditions

                return acc
            },
            {} as Record<string, Filter[]>
        )
    }, [displayedItems])

    const onChangeConditionalVisibilityFilters = useCallback(
        (filters: Record<string, Filter[]>) => {
            onChange((attrs) => {
                const items = attrs.get('items')
                if (!items) return

                for (const item of displayedItems) {
                    const changedFilters = filters[item.sid]
                    if (!changedFilters) continue

                    const existingItem = yUtils.arrayFind<typeof attrs>(
                        items,
                        (f) => f.get('sid') === item.sid
                    )
                    if (existingItem) {
                        existingItem.set('conditions', toYType(changedFilters))
                    } else if (changedFilters.length > 0) {
                        items.push([
                            toYType({
                                sid: item.sid,
                                apiName: item.apiName,
                                conditions: changedFilters,
                            } as HighlightsWidgetItem),
                        ])
                    }
                }
            })
        },
        [onChange, displayedItems]
    )

    if (!object) return null

    return (
        <Box flex column height="full" gap="l">
            <Box px="l" flex center justifyContent="space-between">
                <Headline size="xs" color="text">
                    Fields
                </Headline>
                <Button
                    variant="primary"
                    size="s"
                    startIcon={{ name: 'Plus' }}
                    ref={addFieldTargetRef}
                    onClick={onAddFieldClick}
                >
                    Add new
                </Button>
            </Box>
            <Box px="l">
                <FieldListEditor
                    object={object}
                    fields={fields}
                    selectedItems={displayedItems.map((f) => ({
                        fieldId: f.field._sid,
                        fieldName: f.field.api_name,
                        fieldLabel: f.label || f.field.label,
                        label: f.label || f.field.label,
                        objectId: f.field.object_id,
                        readOnly: false,
                        required: false,
                        conditions: f.conditions,
                        originalItem: f,
                    }))}
                    allowEditFields={true}
                    onUpdate={onItemUpdate}
                    onEdit={onItemEdit as any}
                    allFieldsModeEnabled={false}
                    disallowSections={true}
                    autoHideEditButton={true}
                    allowEditSections={false}
                    hideTopSection={true}
                    showConditionalVisibility={true}
                    conditionalVisibilityFilters={conditionalVisibilityFilters}
                    onConditionalVisibilityFiltersChange={onChangeConditionalVisibilityFilters}
                />
            </Box>
            <LayoutEditorControlSeparator />
            <LayoutEditorVisibilityFiltersControl widget={widget} onChange={onChange} />
            <FieldEditorPopover
                objectId={object?._sid}
                open={isAddFieldOpen}
                target={addFieldTargetRef.current ?? undefined}
                onSuccess={onAddFieldClose}
                onCancel={onAddFieldClose}
                onClose={onAddFieldClose}
                placement="bottom-start"
            />
        </Box>
    )
}

const DEBOUNCE_RATE = 300 // ms

type ItemDetailsControlProps = {
    itemSid: string
}

const ItemDetailsControl: WidgetAdminControlsComponent<
    HighlightsWidgetType,
    ItemDetailsControlProps
> = ({ widget, onChange, itemSid }) => {
    const { fields } = useLayoutEditorContext()

    const field = fields.find((f) => f._sid === itemSid)
    const widgetItem: HighlightsWidgetItem = widget.attrs.items.find((f) => f.sid === itemSid) ?? {
        sid: itemSid,
        apiName: field?.api_name ?? '',
        conditions: [],
    }

    const updateItemAttribute = (attrs: Y.Map<any>, key: string, value: any) => {
        const items = attrs.get('items')

        const item = yUtils.arrayFind<typeof attrs>(items, (f) => f.get('sid') === itemSid)
        if (!!item) {
            item.set(key, value)
        } else if (typeof value !== 'undefined') {
            items.push([
                toYType({
                    ...widgetItem,
                    [key]: value,
                }),
            ])
        }
    }

    const debouncedOnChange = useDebounce(onChange, DEBOUNCE_RATE) as typeof onChange

    const defaultTitle = field?.label || ''
    const [localTitle, setLocalTitle] = useState(widgetItem.label || '')
    const onChangeLabel = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value || ''

        setLocalTitle(value)
        debouncedOnChange((attrs) => {
            updateItemAttribute(attrs, 'label', value)
        })
    }

    const defaultIcon = field ? getFieldIcon(field) : undefined
    const icon = widgetItem?.icon
    const onChangeIcon = (value?: IconValue) => {
        onChange((attrs) => {
            const newValue = value ? toYType(value as HighlightsWidgetItem['icon']) : undefined
            updateItemAttribute(attrs, 'icon', newValue)
        })
    }

    if (!field) return <div />

    return (
        <Box px="l" flex column gap="3xl" height="full" overflowY="auto">
            <Box flex alignItems="flex-start" gap="m">
                <Box noShrink>
                    <Field
                        label="Icon"
                        helperText={!!defaultIcon ? <FieldIcon value={defaultIcon} /> : undefined}
                    >
                        <IconPickerDropdown value={icon} onChange={onChangeIcon} />
                    </Field>
                </Box>
                <Box grow>
                    <Input
                        label="Label"
                        name="label"
                        size="m"
                        autoFocus
                        helperText={defaultTitle}
                        value={localTitle}
                        onChange={onChangeLabel}
                    />
                </Box>
            </Box>
        </Box>
    )
}

type HighlightsStyleControlsProps = {}

const HighlightsStyleControls: WidgetAdminControlsComponent<
    HighlightsWidgetType,
    HighlightsStyleControlsProps
> = ({ widget, onChange }) => {
    const style = widget.attrs.style || 'minimal'

    const onChangeStyle = useCallback(
        (value: string) => {
            const newValue = value || undefined

            onChange((attrs) => {
                attrs.set('style', newValue)
            })
        },
        [onChange]
    )

    const showItemLabel = widget.attrs.showItemLabel ?? true

    const onChangeShowItemLabel = useCallback(
        (value: boolean) => {
            onChange((attrs) => {
                attrs.set('showItemLabel', value)
            })
        },
        [onChange]
    )

    const showItemIcon = widget.attrs.showItemIcon ?? true

    const onChangeShowItemIcon = useCallback(
        (value: boolean) => {
            onChange((attrs) => {
                attrs.set('showItemIcon', value)
            })
        },
        [onChange]
    )

    return (
        <Box px="l" flex column gap="3xl">
            <Field label="Display style">
                <RadioCardGroup
                    value={style}
                    onValueChange={onChangeStyle}
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, 1fr)',
                    }}
                >
                    <RadioCard
                        value="minimal"
                        icon={() => (
                            <BlockStyleIcon style="minimal" isActive={style === 'minimal'} />
                        )}
                    >
                        Minimal
                    </RadioCard>
                    <RadioCard
                        value="boxed"
                        icon={() => <BlockStyleIcon style="boxed" isActive={style === 'boxed'} />}
                    >
                        Boxed
                    </RadioCard>
                </RadioCardGroup>
            </Field>
            <Field
                htmlFor="showItemLabel"
                label="Show field label"
                rightSlotContent={
                    <Toggle
                        id="showItemLabel"
                        checked={showItemLabel}
                        onCheckedChange={onChangeShowItemLabel}
                    />
                }
            />
            <Field
                htmlFor="showItemIcon"
                label="Show field icon"
                rightSlotContent={
                    <Toggle
                        id="showItemIcon"
                        checked={showItemIcon}
                        onCheckedChange={onChangeShowItemIcon}
                    />
                }
            />
        </Box>
    )
}

type BlockStyleIconProps = {
    style: HighlightsWidgetType['attrs']['style']
    isActive: boolean
}

const BlockStyleIcon: React.FC<BlockStyleIconProps> = ({ style, isActive }) => {
    let background: React.ComponentProps<typeof Box>['background'] = isActive
        ? 'theme100'
        : 'gray200'

    let borderColor: React.ComponentProps<typeof Box>['borderColor'] = background
    if (style !== 'minimal') {
        borderColor = isActive ? 'theme300' : 'gray300'
    }

    let itemHeight = '14px'
    if (style === 'minimal') {
        itemHeight = '8px'
    }

    return (
        <Box
            flex
            center
            style={{
                width: '50%',
                height: '14px',
            }}
        >
            <Box
                display="grid"
                width="full"
                gap="xs"
                style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}
            >
                {Array.from({ length: 3 }).map((_, index) => (
                    <Box
                        key={index}
                        width="full"
                        height="full"
                        background={background}
                        borderWidth={1}
                        borderColor={borderColor}
                        style={{ height: itemHeight, borderRadius: '2px' }}
                    />
                ))}
            </Box>
        </Box>
    )
}
